import { useEffect, useState } from 'react'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Kbd, ModKey } from '@/components/ui/kbd'
import { isModKeyPressed } from '@/utils/platform'

/**
 * The 'mod' entry renders as the platform modifier (⌘ on Mac, Ctrl elsewhere).
 */
const SHORTCUT_GROUPS = [
  {
    title: 'Global',
    shortcuts: [
      { keys: ['mod', 'K'], description: 'Open the command palette' },
      { keys: ['mod', 'J'], description: 'Open the assistant panel (Ask Graphit)' },
      { keys: ['?'], description: 'Show this shortcuts help' },
      { keys: ['Esc'], description: 'Close dialogs and menus' },
    ],
  },
  {
    title: 'Forms & wizards',
    shortcuts: [
      { keys: ['mod', '↵'], description: 'Continue or submit (metric wizard, form builder)' },
      { keys: ['mod', 'S'], description: 'Save (data entry, report template editor)' },
    ],
  },
  {
    title: 'Data entry',
    shortcuts: [
      { keys: ['↵'], description: 'Save the current value and move to the next field' },
      { keys: ['↑', '↓'], description: 'Move between fields in a section' },
    ],
  },
  {
    title: 'Command palette',
    shortcuts: [
      { keys: ['↑', '↓'], description: 'Navigate results' },
      { keys: ['↵'], description: 'Open the selected result' },
    ],
  },
]

/**
 * Whether the user is typing in a field, where a bare "?" should
 * insert the character instead of opening this dialog.
 */
function isTypingTarget(target) {
  if (!target || typeof target.tagName !== 'string') {
    return false
  }
  return (
    target.isContentEditable ||
    ['INPUT', 'TEXTAREA', 'SELECT'].includes(target.tagName)
  )
}

/**
 * The element a keystroke actually came from.
 *
 * On a window-level listener, `e.target` is retargeted to the shadow host
 * whenever the event crosses a shadow boundary, so a character typed into an
 * embedded widget arrives looking like it came from that widget's custom
 * element rather than from the <textarea> inside it. composedPath()[0] is the
 * real element.
 */
function getEventSource(e) {
  const path = typeof e.composedPath === 'function' ? e.composedPath() : []
  return path.length > 0 ? path[0] : e.target
}

/**
 * Whether a bare "?" should be left alone rather than opening this dialog.
 *
 * Covers our own inputs, and any keystroke originating inside a shadow root we
 * do not own - third-party widgets render their fields there, and swallowing
 * "?" inside one makes the character impossible to type. That is exactly what
 * happened with the Sentry feedback form (issue #205): users could not put a
 * question mark in their feedback.
 */
function shouldLeaveHelpKeyAlone(e) {
  const source = getEventSource(e)

  if (isTypingTarget(source)) {
    return true
  }

  // Retargeting means the event crossed a shadow boundary. We cannot inspect
  // what is in there, so assume it owns its own keys.
  return source !== e.target
}

function ShortcutKeys({ keys }) {
  return (
    <span className="flex items-center gap-1">
      {keys.map((key) => (
        <Kbd key={key}>{key === 'mod' ? <ModKey /> : key}</Kbd>
      ))}
    </span>
  )
}

/**
 * KeyboardShortcutsDialog - App-wide keyboard shortcuts reference.
 *
 * Opens with "?" (outside of text fields) or Ctrl/⌘+/ (anywhere).
 */
export default function KeyboardShortcutsDialog() {
  const [isOpen, setIsOpen] = useState(false)

  useEffect(() => {
    const handleKeyDown = (e) => {
      const isHelpKey =
        e.key === '?' && !e.ctrlKey && !e.metaKey && !e.altKey && !shouldLeaveHelpKeyAlone(e)
      const isModSlash = e.key === '/' && isModKeyPressed(e)

      if (isHelpKey || isModSlash) {
        e.preventDefault()
        setIsOpen((prev) => !prev)
      }
    }

    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [])

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Keyboard shortcuts</DialogTitle>
          <DialogDescription>
            Shortcuts available across Graphit. Press <Kbd>?</Kbd> anytime to open this list.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-5">
          {SHORTCUT_GROUPS.map((group) => (
            <div key={group.title}>
              <p className="mb-2 text-caption font-semibold text-ink-2">
                {group.title}
              </p>
              <div className="space-y-1.5">
                {group.shortcuts.map((shortcut) => (
                  <div
                    key={shortcut.description}
                    className="flex items-center justify-between gap-4 text-sm"
                  >
                    <span className="text-ink">{shortcut.description}</span>
                    <ShortcutKeys keys={shortcut.keys} />
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  )
}
